import { useNavigate } from 'react-router-dom';
import { TabelaCartoes } from '@/components/common/TabelaCartoes';
import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Skeleton } from '@/components/ui/skeleton';
import { TableSkeleton } from '@/components/common/Skeletons';
import { Smartphone, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import {
  useDashboardMetrics,
  useDeviceActivity,
  useErrorsByType,
  useRecentDevices,
} from '@/hooks/useDashboard';
import { useRealtimeLogs } from '@/hooks/useRealtimeLogs';
import { useRealtimeDevices } from '@/hooks/useRealtimeDevices';
import { useChartColors } from '@/hooks/useChartColors';
import { formatRelativeTime, formatChartDate, isNewUser } from '@/utils/formatters';
import type { ActivityPeriod } from '@/api/endpoints/dashboard';
import { ActiveUsersTrendCard } from '@/components/dashboard/ActiveUsersTrendCard';
import { useUptime } from '@/hooks/useUptime';
import type { UptimeSystem } from '@/api/endpoints/uptime';
import { FirebaseAnalyticsCard } from '@/components/dashboard/FirebaseAnalyticsCard';
import { MetricBand, MetricBandGroup } from '@/components/dashboard/MetricBand';
import { AvatarIniciais, ItemLista, ListaMobile, Selo } from '@/components/common/ListaMobile';
import { getAvatarColor, getUserInitials } from '@/components/common/avatarHelpers';
import { nomePlataforma, rotuloVersao, tempoCurto } from '@/utils/aparelho';

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Latência média da API de produção medida pelo UptimeRobot (janela de 5h).
function apiResponseMs(systems: UptimeSystem[] | undefined): number | null {
  const api =
    systems?.find((s) => /\/\/api\.curraltech\.com/.test(s.url ?? '')) ??
    systems?.find((s) => /\bapi\b/i.test(s.name) && !/test/i.test(s.name));
  return api?.avgResponseMs != null ? Math.round(api.avgResponseMs) : null;
}

export function Dashboard() {
  const navigate = useNavigate();
  const chart = useChartColors();
  const [activityPeriod, setActivityPeriod] = useState<ActivityPeriod>('7d');

  // Tooltip do recharts temático (default vem branco e estoura no dark)
  const tooltipStyles = {
    contentStyle: {
      backgroundColor: chart.tooltipBg,
      border: `1px solid ${chart.tooltipBorder}`,
      borderRadius: 8,
      boxShadow: '0 10px 30px -12px rgb(0 0 0 / 0.45)',
    },
    labelStyle: { color: chart.textTitle, fontWeight: 600, marginBottom: 4 },
    itemStyle: { color: chart.textBody },
  };

  // Cor semântica (traffic-light) por tipo de erro
  const barColor = (type: string) => {
    switch ((type || '').toLowerCase()) {
      case 'crash':
        return chart.error;
      case 'exception':
        return chart.warning;
      case 'network':
        return chart.info;
      case 'database':
        return chart.tertiary;
      default:
        return chart.primary;
    }
  };

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useDashboardMetrics();
  const { data: activityData, isLoading: activityLoading } = useDeviceActivity(activityPeriod);
  const { data: errorsData, isLoading: errorsLoading } = useErrorsByType();
  const { data: recentDevicesData, isLoading: devicesLoading } = useRecentDevices(5);
  const { data: uptimeData, isError: uptimeError } = useUptime();

  const { criticalErrors, isConnected } = useRealtimeLogs(50);

  useRealtimeDevices();

  useEffect(() => {
    if (criticalErrors.length > 0) {
      refetchMetrics();
    }
  }, [criticalErrors.length, refetchMetrics]);

  if (metricsError) {
    return (
      <Layout breadcrumbs={[{ label: 'Página Inicial', path: '/' }, { label: 'Dashboard' }]}>
        <div className="min-h-full bg-bg-main p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-error mb-4" />
              <h2 className="mb-2 text-xl font-semibold text-text-title">
                Erro ao carregar dashboard
              </h2>
              <p className="text-sm text-text-secondary">
                Não foi possível carregar os dados. Tente novamente.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const metrics = {
    totalDevices: metricsData?.totalDevices ?? 0,
    activeDevices: metricsData?.activeDevices ?? 0,
    totalErrors: metricsData?.totalErrors ?? 0,
    criticalErrors: metricsData?.criticalErrors ?? 0,
    totalUsers: metricsData?.totalUsers ?? 0,
    activeUsers: metricsData?.activeUsers ?? 0,
    unresolvedErrors: metricsData?.unresolvedErrors ?? 0,
    onlineNow: metricsData?.onlineNow ?? 0,
  };
  const apiLatency = apiResponseMs(uptimeData?.systems);

  const recentDevices = recentDevicesData?.devices || [];
  const recentOnline = recentDevices.filter((d) => d.status === 'online').length;

  return (
    <Layout breadcrumbs={[{ label: 'Página Inicial', path: '/' }, { label: 'Dashboard' }]}>
      <div className="min-h-full bg-bg-main p-3 sm:p-4 lg:p-6">
        <div className="flex flex-col gap-4 lg:gap-6">
          {/* Título */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="max-md:hidden text-2xl font-semibold tracking-tight text-text-title sm:text-3xl">
              Dashboard de Monitoramento
            </h1>
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
                isConnected ? 'bg-online/10 text-online' : 'bg-offline/10 text-offline'
              }`}
            >
              {isConnected ? <Wifi size={16} /> : <WifiOff size={16} />}
              <span>{isConnected ? 'Tempo Real Ativo' : 'Tempo Real Desconectado'}</span>
            </div>
          </div>

          {/* Faixas de métricas: Agora / Analytics / Usuários */}
          <MetricBandGroup>
            <MetricBand
              title="Agora"
              caption="tempo real"
              loading={metricsLoading}
              metrics={[
                {
                  label: 'Dispositivos ativos',
                  value: metrics.activeDevices.toLocaleString(),
                  hint: `de ${metrics.totalDevices.toLocaleString()}`,
                },
                {
                  label: 'Usuários ativos',
                  value: metrics.activeUsers.toLocaleString(),
                  hint: `de ${metrics.totalUsers.toLocaleString()}`,
                  onClick: () => navigate('/admin'),
                },
                {
                  label: 'Online agora',
                  value: metrics.onlineNow.toLocaleString(),
                  hint: 'em 5 min',
                },
                {
                  label: 'Erros críticos',
                  value: metrics.criticalErrors.toLocaleString(),
                  hint: `${metrics.unresolvedErrors.toLocaleString()} abertos`,
                  valueClassName: 'text-error',
                  onClick: () => navigate('/errors'),
                },
                {
                  label: 'Resposta da API',
                  value: apiLatency != null ? apiLatency.toLocaleString() : '—',
                  unit: apiLatency != null ? 'ms' : undefined,
                  hint: uptimeError ? 'sem acesso' : 'média 5h',
                  onClick: uptimeError ? undefined : () => navigate('/uptime'),
                },
              ]}
            />
            <FirebaseAnalyticsCard />
            <ActiveUsersTrendCard />
          </MetricBandGroup>

          {/* Gráficos */}
          <div className="grid grid-cols-1 items-stretch lg:grid-cols-5 gap-6">
            {/* Gráfico de Linha - Atividade de Dispositivos */}
            <div className="lg:col-span-3 flex h-full flex-col gap-4 rounded-lg p-6 bg-card border border-border shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-xl font-semibold text-text-title">Dispositivos no dia</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '7d', label: 'Últimos 7 dias' },
                    { value: '30d', label: 'Últimos 30 dias' },
                    { value: '3m', label: 'Últimos 3 meses' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setActivityPeriod(value as typeof activityPeriod)}
                      aria-pressed={activityPeriod === value}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                        activityPeriod === value
                          ? 'bg-primary text-bg-main shadow-sm'
                          : 'bg-muted text-text-secondary hover:bg-bg-elevated hover:text-text-body'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {activityLoading ? (
                <Skeleton className="h-[300px] w-full rounded-lg" />
              ) : activityData && activityData.length > 0 ? (
                <div className="min-h-[300px] w-full flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={activityData}
                      margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="devicesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chart.primary} stopOpacity={0.2} />
                          <stop offset="100%" stopColor={chart.primary} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={chart.grid} vertical={false} />
                      <XAxis
                        dataKey="date"
                        stroke={chart.axis}
                        tick={{ fill: chart.axis }}
                        fontSize={12}
                        tickLine={false}
                        axisLine={{ stroke: chart.grid }}
                        minTickGap={16}
                        tickFormatter={(value) => formatChartDate(value)}
                      />
                      <YAxis
                        stroke={chart.axis}
                        tick={{ fill: chart.axis }}
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                        width={32}
                      />
                      <Tooltip
                        {...tooltipStyles}
                        cursor={{ stroke: chart.grid }}
                        labelFormatter={(value) => formatChartDate(value as string)}
                      />
                      <Area
                        type="monotone"
                        dataKey="devices"
                        name="Dispositivos"
                        stroke={chart.primary}
                        strokeWidth={2}
                        fill="url(#devicesGradient)"
                        dot={false}
                        activeDot={{ r: 5, fill: chart.primary }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-text-secondary">Sem dados disponíveis</p>
                </div>
              )}
            </div>

            {/* Gráfico de Barras - Erros por Tipo */}
            <div className="lg:col-span-2 flex h-full flex-col gap-4 rounded-lg p-6 bg-card border border-border shadow-sm">
              <h3 className="text-xl font-semibold text-text-title">
                Erros por Tipo (Últimas 24h)
              </h3>
              {errorsLoading ? (
                <Skeleton className="h-[300px] w-full rounded-lg" />
              ) : errorsData && errorsData.length > 0 ? (
                <div className="min-h-[300px] w-full flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={errorsData}>
                      <CartesianGrid stroke={chart.grid} vertical={false} />
                      <XAxis
                        dataKey="type"
                        stroke={chart.axis}
                        tick={{ fill: chart.axis }}
                        fontSize={12}
                        tickLine={false}
                        axisLine={{ stroke: chart.grid }}
                      />
                      <YAxis
                        stroke={chart.axis}
                        tick={{ fill: chart.axis }}
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        width={32}
                      />
                      <Tooltip {...tooltipStyles} cursor={{ fill: chart.grid }} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={48}>
                        {errorsData.map((entry: { type: string }, i: number) => (
                          <Cell key={i} fill={barColor(entry.type)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-text-secondary">Sem dados disponíveis</p>
                </div>
              )}
            </div>
          </div>

          {/* Usuários ativos recentes no celular: lista no padrão de app */}
          <ListaMobile
            className="lg:hidden"
            titulo="Usuários ativos recentes"
            resumo={
              !devicesLoading && (
                <span className="text-xs tabular-nums text-text-secondary">
                  {recentOnline} online
                </span>
              )
            }
          >
            {devicesLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <ItemLista
                  key={i}
                  inicio={<Skeleton className="size-11 rounded-full" />}
                  titulo={<Skeleton className="h-4 w-32" />}
                  apoio={<Skeleton className="h-3 w-24" />}
                />
              ))
            ) : recentDevices.length > 0 ? (
              recentDevices.map((device) => {
                const online = device.status === 'online';
                return (
                  <ItemLista
                    key={device.id}
                    inicio={
                      <AvatarIniciais
                        iniciais={getUserInitials(device.userName || '?')}
                        online={online}
                        className={getAvatarColor(device.userId)}
                      />
                    }
                    titulo={device.userName}
                    tituloExtra={isNewUser(device.userCreatedAt) && <Selo tom="info">Novo</Selo>}
                    apoio={[device.model, nomePlataforma(device.platform)]
                      .filter(Boolean)
                      .join(' · ')}
                    fim={
                      <>
                        <span className={online ? 'font-medium text-online' : undefined}>
                          {tempoCurto(device.lastActivity)}
                        </span>
                        {device.appVersion && (
                          <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-text-secondary">
                            {rotuloVersao(device.appVersion)}
                          </span>
                        )}
                      </>
                    }
                  />
                );
              })
            ) : (
              <li className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                <Smartphone className="size-8 text-text-hint" />
                <p className="text-sm text-text-secondary">Nenhum usuário ativo recente</p>
              </li>
            )}
          </ListaMobile>

          {/* Tabela de Usuários (desktop) */}
          <div className="hidden overflow-hidden rounded-lg border border-border bg-card shadow-sm lg:block">
            <div className="p-4 border-b border-border">
              <h3 className="text-xl font-semibold text-text-title">Usuários Ativos Recentes</h3>
            </div>

            <div className="overflow-x-auto">
              {devicesLoading ? (
                <TableSkeleton rows={5} columns={['w-20', 'w-32', 'w-28', 'w-16', 'w-24']} />
              ) : recentDevices.length > 0 ? (
                <TabelaCartoes className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Status', 'Usuário', 'Dispositivo', 'Versão App', 'Última Atividade'].map(
                        (col) => (
                          <th
                            key={col}
                            className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-title"
                          >
                            {col}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-card">
                    {recentDevices.map((device) => (
                      <tr key={device.id} className="transition-colors hover:bg-muted/50">
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                              device.status === 'online'
                                ? 'bg-online/10 text-online'
                                : 'bg-offline/10 text-offline'
                            }`}
                          >
                            <span className="size-1.5 rounded-full bg-current" />
                            {device.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-text-title">
                              {device.userName}
                            </span>
                            {isNewUser(device.userCreatedAt) && (
                              <span className="inline-flex items-center rounded-full bg-info/10 px-2 py-0.5 text-xs font-medium text-info">
                                Novo
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-sm text-text-body">{device.model}</div>
                          <div className="text-xs capitalize text-text-secondary">
                            {device.platform}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 font-mono text-sm tabular-nums text-text-body">
                          {device.appVersion}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-text-secondary">
                          {formatRelativeTime(device.lastActivity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TabelaCartoes>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                  <Smartphone className="size-8 text-text-hint" />
                  <p className="text-sm text-text-secondary">Nenhum usuário ativo recente</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
